import S from 's-js';
import { Client } from 'lib/Ajax';
import { ValidationWatcher } from 'lib/validationWatcher';
import { formatFileSize } from 'lib/formatFileSize';

const FILE_INPUT_ID_PREFIX = '__pc_upload_file_input_';

let _fileInputId = 0;

function nextFileInputId() {
    return _fileInputId++;
}

export const INVALID_EXTENSION = 'InvalidExtension',
    FILE_TOO_BIG = 'FileTooBig';

export interface UploadSettings {
    AllowedExtensions : () => string[];
    MaximumFileSize : () => number; // Maximum size in KB
}

export interface UploadFileParams<TResponseData> {
    onComplete : (data : TResponseData) => void;
}

export function UploadFile<TResponseData>(
    client : Client<{}, TResponseData>,
    settings : UploadSettings,
    invalidExtensionMessage : string,
    { onComplete } : UploadFileParams<TResponseData>
) {
    const validation = ValidationWatcher(client),
        invalidExtension = S.data(false),
        fileTooBig = S.data(false),
        fileInputId = S.value(nextFileInputId());
    let uploading = false;

    let extendedValidation = S(() => {
        let errors = validation(),
            fileTooBigMsg = `Please pick a file that's ${formatFileSize(settings.MaximumFileSize() * 1024, 0)} or less.`;

        if (invalidExtension()) errors[INVALID_EXTENSION] = {
            Field: INVALID_EXTENSION,
            RawErrors: [invalidExtensionMessage],
            ErrorTexts: [invalidExtensionMessage],
            ErrorText: invalidExtensionMessage
        }
        if (fileTooBig()) errors[FILE_TOO_BIG] = {
            Field: FILE_TOO_BIG,
            RawErrors: [fileTooBigMsg],
            ErrorTexts: [fileTooBigMsg],
            ErrorText: fileTooBigMsg
        }

        return errors;
    });

    return {
        UploadFile,
        AllowedExtensions: () => '.' + settings.AllowedExtensions().join(',.'),
        Validation: extendedValidation,
        ResetValidation: ResetValidation,
        FileInputId : () => FILE_INPUT_ID_PREFIX + fileInputId()
    };

    function UploadFile(file : File) {
        if (uploading) return;
        uploading = true;
        // validate extension
        let ext = GetFileExtension(file.name).toLowerCase();
        let extMatch = S.sample(settings.AllowedExtensions).some(_ext =>
            _ext.toLowerCase() === ext);
        if (!extMatch) {
            S.freeze(() => {
                invalidExtension(true);
                clearFilePicker();
            });
            return;
        }
        // validate file size
        if (file.size / 1024 > S.sample(settings.MaximumFileSize)) {
            S.freeze(() => {
                fileTooBig(true);
                clearFilePicker();
            });
            return;
        }

        let formData = new FormData();
        formData.append('file', file);
        client.Post(formData)
            .then(onComplete)
            .finally(() => {
                clearFilePicker();
            });

        // TODO: progress bar?

        // TODO: make upload abortable?
    }

    function clearFilePicker() {
        uploading = false;
        // We use the fileInputId to drive a React key.
        // So incrementing the id will force React to render a new file input, thus clearing state from the old input.
        fileInputId(nextFileInputId());
    }

    function GetFileExtension(filename : string) {
        let match = filename.match(/\.([^.]*)$/);
        return match ? match[1] : '';
    }

    function ResetValidation() {
        S.freeze(() => {
            validation({});
            invalidExtension(false);
            fileTooBig(false);
        });
    }
}
export type UploadFile = ReturnType<typeof UploadFile>;


// WEBPACK FOOTER //
// ./apps/portcall/uploadfile/model.ts